import { Network } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { aggregatorHomePath, ApiError, apiErrorMessage, getAggregatorToken, setAggregatorToken } from '../../api/client'
import { aggregatorPortalApi } from '../../api/endpoints'
import { JoinCodeStep } from '../../components/auth/JoinCodeStep'
import { ChipGroup } from '../../components/ChipGroup'
import { ErrorMessage } from '../../components/ErrorMessage'
import { Logo } from '../../components/Logo'
import { StateSelect } from '../../components/StateSelect'
import type { AggregatorOrgRegisterRequest } from '../../types/api'
import { AGGREGATOR_KIND_CHOICES } from './aggregatorKinds'
import { isValidMobile, MOBILE_PATTERN } from '../../utils/validation'

/**
 * The aggregator portal's front door - sign in, or register an organisation.
 *
 * <p>Its own page and its own session, like /admin: an aggregator is an organisation that brings
 * learners and tutors in, not a participant, so it never sees the participant app.
 */
export function AggregatorPortalAuthPage({ mode }: { mode: 'login' | 'register' }) {
  if (getAggregatorToken()) return <Navigate to={aggregatorHomePath()} replace />

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-sun-hero px-4 py-6">
      <div className={`w-full ${mode === 'register' ? 'max-w-xl' : 'max-w-md'}`}>
        <div className="text-center">
          <Link to="/" aria-label="YellowDots home" className="inline-block">
            <Logo size="page" />
          </Link>
          <h1 className="mt-4 flex items-center justify-center gap-2 text-3xl font-extrabold tracking-tight text-ink-900">
            <Network size={24} className="shrink-0 text-sun-600" />
            Partner Portal
          </h1>
          <p className="mt-1 text-ink-500">
            For NGOs, skilling centres and community organisations that bring learners and tutors onto
            YellowDots.
          </p>
        </div>

        <div className="card mt-5 p-5 shadow-float sm:p-6">{mode === 'login' ? <LoginForm /> : <RegisterForm />}</div>

        <p className="mx-auto mt-5 max-w-sm text-center text-sm leading-relaxed text-ink-500">
          {mode === 'login' ? (
            <>
              New organisation?{' '}
              <Link to="/aggregator-portal/register" className="font-semibold text-ink-900 hover:underline">
                Register here
              </Link>
              <br />
              Coordinators: sign in with the email your organisation invited. New coordinator?{' '}
              <Link to="/aggregator-portal/join" className="font-semibold text-ink-900 hover:underline">
                Join with your invitation code
              </Link>{' '}
              or the link in your email.
            </>
          ) : (
            <>
              Already registered?{' '}
              <Link to="/aggregator-portal/login" className="font-semibold text-ink-900 hover:underline">
                Sign in
              </Link>
            </>
          )}
        </p>
      </div>
    </div>
  )
}

function LoginForm() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  // Set once a code has been sent: the form swaps to the code step for this address.
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // No organisation or coordinator uses this email: say so and point to registering, no code step.
  const [notRegistered, setNotRegistered] = useState<string | null>(null)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    setNotRegistered(null)
    try {
      const address = email.trim()
      await aggregatorPortalApi.requestLoginCode(address)
      setSentTo(address)
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setNotRegistered(apiErrorMessage(err, 'No partner account uses this email.'))
      else setError(apiErrorMessage(err, 'Could not send a join code'))
    } finally {
      setSubmitting(false)
    }
  }

  if (sentTo) {
    return (
      <JoinCodeStep
        sentTo={sentTo}
        onVerify={async (code) => {
          const res = await aggregatorPortalApi.login(sentTo, code)
          setAggregatorToken(res.token, res.accountType)
          // An organisation lands on its own dashboard; a coordinator on its uploads.
          navigate(aggregatorHomePath(), { replace: true })
        }}
        onResend={async () => {
          await aggregatorPortalApi.requestLoginCode(sentTo)
        }}
        onBack={() => setSentTo(null)}
        submitLabel="Sign in"
      />
    )
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3.5">
      <label className="block">
        <span className="label label-required">Email</span>
        <input
          required
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value)
            setNotRegistered(null)
          }}
          className="input"
        />
      </label>
      <ErrorMessage message={error} />
      {notRegistered && (
        <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">{notRegistered}</p>
      )}
      {/* Off while the address has no account - it stays off until the email is changed. */}
      <button type="submit" disabled={submitting || notRegistered !== null} className="btn-sun w-full disabled:opacity-40">
        {submitting ? 'Sending...' : 'Send join code'}
      </button>
    </form>
  )
}

function RegisterForm() {
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [kindLabel, setKindLabel] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [ownerName, setOwnerName] = useState('')
  const [website, setWebsite] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // The body a code was sent for. Nothing exists until the code is entered; resend sends it again.
  const [pending, setPending] = useState<AggregatorOrgRegisterRequest | null>(null)

  function validate(): string | null {
    if (!name.trim()) return "Enter your organisation's name"
    if (!kindLabel) return 'Choose what kind of organisation you are'
    if (!ownerName.trim()) return 'Enter your name'
    if (!isValidMobile(phone)) return 'Enter a ten-digit mobile number'
    if (!email.trim()) return 'Enter an email address'
    if (!termsAccepted) return 'Accept the terms to finish'
    return null
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    const problem = validate()
    if (problem) {
      setError(problem)
      return
    }
    setSubmitting(true)
    setError(null)
    const body: AggregatorOrgRegisterRequest = {
      name: name.trim(),
      kind: AGGREGATOR_KIND_CHOICES.find((k) => k.label === kindLabel)?.kind ?? 'OTHER',
      city: city.trim() || undefined,
      state: state.trim() || undefined,
      website: website.trim() || undefined,
      ownerName: ownerName.trim(),
      phone,
      email: email.trim(),
      termsAccepted,
    }
    try {
      await aggregatorPortalApi.register(body)
      setPending(body)
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not complete the registration'))
    } finally {
      setSubmitting(false)
    }
  }

  if (pending) {
    return (
      <JoinCodeStep
        sentTo={pending.email}
        onVerify={async (code) => {
          const res = await aggregatorPortalApi.verifyRegistration(pending.email, code)
          setAggregatorToken(res.token, res.accountType)
          navigate(aggregatorHomePath(), { replace: true })
        }}
        onResend={async () => {
          await aggregatorPortalApi.register(pending)
        }}
        onBack={() => setPending(null)}
        submitLabel="Register organisation"
      />
    )
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <label className="block">
        <span className="label label-required">Organisation name</span>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <ChipGroup
        label="Type of organisation"
        options={AGGREGATOR_KIND_CHOICES.map((k) => k.label)}
        value={kindLabel}
        onChange={setKindLabel}
        required
      />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="label">City</span>
          <input className="input" value={city} onChange={(e) => setCity(e.target.value)} />
        </label>
        <label className="block">
          <span className="label">State</span>
          <StateSelect value={state} onChange={setState} />
        </label>
      </div>
      <label className="block">
        <span className="label">Website</span>
        <input className="input" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://yourorg.in" />
      </label>
      <label className="block">
        <span className="label label-required">Your name</span>
        <input className="input" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} />
        <span className="mt-1 block text-xs text-ink-400">
          You run the organisation&apos;s account: you invite coordinators and track everything they bring in.
        </span>
      </label>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="label label-required">Mobile</span>
          <input
            className="input"
            inputMode="numeric"
            maxLength={10}
            pattern={MOBILE_PATTERN}
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
          />
        </label>
        <label className="block">
          <span className="label label-required">Email (your login)</span>
          <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
      </div>
      <label className="flex items-start gap-2 pt-1 text-sm text-ink-700">
        <input type="checkbox" className="mt-1" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} />
        <span>
          I accept the{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer" className="font-semibold text-dot-700 underline underline-offset-2">
            Terms &amp; Conditions
          </a>{' '}
          and{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="font-semibold text-dot-700 underline underline-offset-2">
            Privacy Policy
          </a>
          , and confirm my organisation has each person&apos;s consent (a parent&apos;s, for anyone under
          18) before uploading their details.
        </span>
      </label>
      <ErrorMessage message={error} />
      <button type="submit" disabled={submitting} className="btn-sun w-full">
        {submitting ? 'Sending...' : 'Join the network'}
      </button>
    </form>
  )
}
